import styles from './RmCard.module.scss';
import AlertsTooltip from './default-tooltips/AlertsTooltip';
import EmergingRmsTooltip from './default-tooltips/EmergingRmsTooltip';
import rmIconGreen from './images/rm-icon-green.svg';
import rmIconGrey from './images/rm-icon-grey.svg';
import rmIconRed from './images/rm-icon-red.svg';
import rmIconYellow from './images/rm-icon-yellow.svg';
import ChartWrapper from 'components/chart-wrapper/ChartWrapper';
import ReactECharts from 'echarts-for-react';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectFiltersDisplayMapping,
    selectMajorThreshold,
    selectMinorThreshold,
    selectTimeslicerEndMonth,
    selectTimeslicerIsSameMonth,
    selectTimeslicerStartMonth,
    selectZoneValue
} from 'selectors/selectors';
import {
    FilterType,
    RMCardGroupDataType,
    RMCardGroupTooltipType
} from 'types/types';
import {
    highestSpikeTrendAnalysisChartOptions,
    scoreDecompositionChartOptions
} from 'utils/charts';
import { useLocation } from 'react-router-dom';
import {
    fetchFilteredDropdownValues,
    updateFilter,
    updateTimeslicerStartEndValues
} from 'slices/FiltersSlice';
import { generateRequestParamsForFilterApi } from 'utils/api';
import {
    BASE_ROUTE,
    MARKET_ROUTE,
    RM_ROUTE,
    TEAM_ROUTE
} from 'constants/routes';
import { calculateStartEndMonth } from 'utils/common';
import { decreaseEndDateByOneMonth } from 'utils/helpers';

const RmCard = (props) => {
    const {
        rmData,
        dataType,
        breachCategory,
        tooltipType = RMCardGroupTooltipType.Default,
        isLastCardInGroup = false,
        isBottomGroup,
        navigateTo
    } = props;
    const dispatch = useDispatch();
    const { pathname: route } = useLocation();

    const [showStats, setShowStats] = useState(false);
    const zoneValue = useSelector(selectZoneValue);
    const minorThreshold = useSelector(selectMinorThreshold);
    const majorThreshold = useSelector(selectMajorThreshold);
    const tsStart = useSelector(selectTimeslicerStartMonth);
    const tsEnd = useSelector(selectTimeslicerEndMonth);
    const isSameMonth = useSelector(selectTimeslicerIsSameMonth);
    const displayMapping = useSelector(selectFiltersDisplayMapping);

    const { rm: rmList } = displayMapping;
    const getRmIcon = () => {
        switch (dataType) {
            case RMCardGroupDataType.Default:
            case RMCardGroupDataType.Alerts:
                return <img src={rmIconGrey} alt="" />;
            case RMCardGroupDataType.TopFiveRms:
            case RMCardGroupDataType.TopFiveRmsSpike:
            case RMCardGroupDataType.TopFiveEmergingModerateRm:
            case RMCardGroupDataType.TopFiveEmergingMajorRm:
                if (getScore() >= majorThreshold)
                    return <img src={rmIconRed} alt="" />;
                if (getScore() <= minorThreshold)
                    return <img src={rmIconGreen} alt="" />;
                if (getScore() < majorThreshold && getScore() > minorThreshold)
                    return <img src={rmIconYellow} alt="" />;
                break;
            default:
                return <img src={rmIconGrey} alt="" />;
        }
    };

    const getEmployeeId = () => {
        switch (dataType) {
            case RMCardGroupDataType.Default:
            case RMCardGroupDataType.Alerts:
                return rmData.rmEmployeeId;
            case RMCardGroupDataType.TopFiveRms:
                return rmData.dimension;
            case RMCardGroupDataType.TopFiveRmsSpike:
            case RMCardGroupDataType.TopFiveEmergingModerateRm:
            case RMCardGroupDataType.TopFiveEmergingMajorRm:
                return rmData.rm;
            default:
                return rmData.rmEmployeeId;
        }
    };

    const getScore = () => {
        switch (dataType) {
            case RMCardGroupDataType.Default:
            case RMCardGroupDataType.TopFiveRms:
                return rmData.score;
            case RMCardGroupDataType.Alerts:
                return rmData.percentAlerts;
            case RMCardGroupDataType.TopFiveRmsSpike:
            case RMCardGroupDataType.TopFiveEmergingModerateRm:
            case RMCardGroupDataType.TopFiveEmergingMajorRm:
                return rmData.endMonthScore;
            default:
                return rmData.score;
        }
    };

    const handleRmCardOnClick = () => {
        if ([BASE_ROUTE, MARKET_ROUTE, TEAM_ROUTE].includes(route)) {
            const rmId = getEmployeeId();
            const rmValueLabel = rmList.find((r) => r.label === rmId);
            if (Object.keys(rmValueLabel).length !== 0) {
                if (
                    [
                        RMCardGroupDataType.TopFiveRmsSpike,
                        RMCardGroupDataType.TopFiveEmergingMajorRm,
                        RMCardGroupDataType.TopFiveEmergingModerateRm
                    ].includes(dataType)
                ) {
                    dispatch(
                        updateTimeslicerStartEndValues({
                            start: decreaseEndDateByOneMonth(tsEnd),
                            end: tsEnd
                        })
                    );
                }

                dispatch(
                    updateFilter({
                        value: rmValueLabel.value,
                        label: rmValueLabel.label,
                        filterType: FilterType.RM
                    })
                );

                const requestParamsObj = generateRequestParamsForFilterApi(
                    tsStart ? tsStart.epoch : null,
                    tsEnd ? tsEnd.epoch : null,
                    zoneValue,
                    null,
                    null,
                    rmValueLabel.value
                );
                const enrichedParamsObj = {
                    requestParams: {
                        ...requestParamsObj.requestParams,
                        selectedFilterType: FilterType.RM,
                        label: rmValueLabel.label,
                        value: rmValueLabel.value
                    }
                };

                dispatch(fetchFilteredDropdownValues(enrichedParamsObj));
                navigateTo(route, RM_ROUTE);
            }
        }
    };

    const dates = calculateStartEndMonth(tsStart, tsEnd, isSameMonth);

    const isTooltipContainsFiveBar =
        rmData.s1Score > 0 &&
        rmData.s2Score > 0 &&
        rmData.s3Score > 0 &&
        rmData.s4Score > 0 &&
        rmData.correlationScore > 0;

    return (
        <div
            className={styles.wrapper}
            onMouseOver={() => setShowStats(true)}
            onMouseOut={() => setShowStats(false)}
            onClick={handleRmCardOnClick}
        >
            <div className={styles.contentContainer}>
                <div className={styles.iconContainer}>{getRmIcon()}</div>
                <div className={styles.rmIdTextContainer}>
                    {getEmployeeId()}
                </div>
                <div className={styles.scoreContainer}>
                    {dataType === RMCardGroupDataType.Alerts
                        ? `${getScore().toFixed(1)}%`
                        : getScore()}
                </div>
                {dataType === RMCardGroupDataType.TopFiveRmsSpike && (
                    <div className={styles.spikeContainer}>
                        ({rmData.spike})
                    </div>
                )}
            </div>
            {showStats && tooltipType === RMCardGroupTooltipType.Default && (
                <div
                    className={
                        isBottomGroup
                            ? styles.bottomRmStatsContainer
                            : styles.rmStatsContainer
                    }
                    style={
                        isLastCardInGroup ? { right: '10%' } : { left: '80%' }
                    }
                >
                    {dataType === RMCardGroupDataType.Alerts && (
                        <AlertsTooltip
                            breachCategory={breachCategory}
                            data={rmData}
                        />
                    )}
                    {(dataType ===
                        RMCardGroupDataType.TopFiveEmergingModerateRm ||
                        dataType ===
                            RMCardGroupDataType.TopFiveEmergingMajorRm) && (
                        <EmergingRmsTooltip data={rmData} dataType={dataType} />
                    )}
                </div>
            )}
            {showStats &&
                dataType === RMCardGroupDataType.TopFiveRms &&
                tooltipType === RMCardGroupTooltipType.ScoreDecomposition && (
                    <div
                        className={styles.rmScoreDecomposition}
                        style={
                            isLastCardInGroup
                                ? { right: '80%' }
                                : { left: '80%' }
                        }
                    >
                        <ChartWrapper
                            comboClass={styles.rightMargin}
                            height={'auto'}
                        >
                            <ReactECharts
                                option={scoreDecompositionChartOptions(rmData)}
                                style={
                                    isTooltipContainsFiveBar
                                        ? { width: '300px', height: '330px' }
                                        : { width: '300px' }
                                }
                            ></ReactECharts>
                        </ChartWrapper>
                    </div>
                )}
            {showStats &&
                dataType === RMCardGroupDataType.TopFiveRmsSpike &&
                tooltipType === RMCardGroupTooltipType.TrendAnalysis && (
                    <div
                        className={styles.trendAnalysisWrapper}
                        style={
                            isLastCardInGroup
                                ? { right: '80%' }
                                : { left: '80%' }
                        }
                    >
                        <ChartWrapper width={'317px'} height={'283px'}>
                            <ReactECharts
                                option={highestSpikeTrendAnalysisChartOptions(
                                    rmData,
                                    dates
                                )}
                                style={{ width: 317, height: 283 }}
                            ></ReactECharts>
                        </ChartWrapper>
                    </div>
                )}
        </div>
    );
};

RmCard.propTypes = {
    breachCategory: PropTypes.string,
    rmData: PropTypes.shape({
        rm: PropTypes.string,
        rmEmployeeId: PropTypes.string,
        dimension: PropTypes.string,
        totalAlerts: PropTypes.number,
        percentAlerts: PropTypes.number,
        rank: PropTypes.number,
        score: PropTypes.number,
        spike: PropTypes.number,
        rmRegion: PropTypes.string,
        rmGroup: PropTypes.string,
        startMonthScore: PropTypes.number,
        endMonthScore: PropTypes.number,
        s1Score: PropTypes.number,
        s2Score: PropTypes.number,
        s3Score: PropTypes.number,
        s4Score: PropTypes.number,
        correlationScore: PropTypes.number
    }),
    dataType: PropTypes.oneOf([
        'default',
        'topFiveRms',
        'topFiveEmergingModerateRm',
        'topFiveEmergingMajorRm',
        'topFiveRmsSpike',
        'alerts'
    ]),
    tooltipType: PropTypes.oneOf([
        'default',
        'scoreDecomposition',
        'trendAnalysis'
    ]),
    isLastCardInGroup: PropTypes.bool,
    isBottomGroup: PropTypes.bool,
    navigateTo: PropTypes.func
};

export default RmCard;
